import {
  UNIFIED_CUSTOMER_SERVICE_PHONE_NUMBER,
  URL_CONTACTUS,
} from '@seek/adv-constants';
import { brandAndCountryFromZone } from '@seek/audience-zones';
import {
  type Locale,
  getLanguageFromLocale,
  createAbsoluteUrl,
  getZoneFromSite,
  siteConfig,
} from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  List,
  Heading,
  Stack,
  Text,
  TextLink,
  PageBlock,
  Card,
  Box,
} from 'braid-design-system';

import { trackPageLoad } from 'src/modules/AnalyticsTracker';
import type { EmployerSiteName, HeaderInfoFetchError } from 'src/types';
import { getCountryFromSite } from 'src/utils/config';
import { isEmployerANZSite } from 'src/utils/isEmployerANZSite';

import translations from './.vocab';
import { ErrorSVG } from './svg/ErrorSVG';

interface ErrorPageProps {
  error: HeaderInfoFetchError;
  site: EmployerSiteName;
  locale: Locale;
}

const ErrorPage = ({ site, locale, error }: ErrorPageProps) => {
  const { t } = useTranslations(translations);
  const showCustomerServicePhoneNo = isEmployerANZSite(site);
  const contactCustomerServiceUrl = createAbsoluteUrl({
    product: 'employer',
    country: getCountryFromSite(site),
    path: URL_CONTACTUS,
    language: getLanguageFromLocale(locale),
  });
  const currentSite = siteConfig[site];
  const zone = site ? getZoneFromSite(site) : undefined;
  const brand = zone ? brandAndCountryFromZone(zone)?.[0] : undefined;

  trackPageLoad('error_page_displayed', {
    currentPage: 'dashboard',
    siteSection: 'home',
    siteCountry: currentSite?.classification?.country,
    siteLanguage: getLanguageFromLocale(locale),
    brand,
    zone: site ? getZoneFromSite(site) : undefined,
    errorMessage: error.message,
  });

  return (
    <Box paddingTop="xlarge">
      <PageBlock width="small">
        <Card>
          <Stack space="large" align="center">
            <ErrorSVG />

            <Heading level="2">{t('Can we try that again')}</Heading>

            <Text>{t('Bear with us')} </Text>

            <Stack space="medium" align="left">
              <Text>{t('Step try')}</Text>
              <List type="number" space="small">
                <Text>{t('Step refresh page')}</Text>
                <Text>{t('Step clear browser history')}</Text>
              </List>
            </Stack>

            <Text>
              {t('Reach out Customer Service')} {t('Contact on')}{' '}
              {showCustomerServicePhoneNo ? (
                <TextLink
                  href={`tel:${UNIFIED_CUSTOMER_SERVICE_PHONE_NUMBER[site]}`}
                >
                  {UNIFIED_CUSTOMER_SERVICE_PHONE_NUMBER[site]}
                </TextLink>
              ) : (
                <TextLink href={contactCustomerServiceUrl}>
                  {t('Contact Customer Service')}
                </TextLink>
              )}
            </Text>
          </Stack>
        </Card>
      </PageBlock>
    </Box>
  );
};

export default ErrorPage;
